import { useState, useEffect, useCallback, useRef } from "react";
import { message, Spin } from "antd";
import { useLanguage } from "../../../../context/LanguageContext";
import useContestService from "../../../../services/contestService";
import useFeedbackFormService from "../../../../services/feedbackFormService";
import {
  giveAwayContent,
  replaceTemplate,
  getPlaceText,
} from "../../../../utils/giveaAwayContent";

// components/giveaways/GiveawayForm.jsx
export function GiveawayForm({
  open,
  onCancel,
  onSave,
  editingGiveaway,
  loading = false,
  contestService, // Optional: Externe contestService Instanz
}) {
  const {
    createContest,
    updateContest,
    deleteContest,
    loading: serviceLoading,
    error: serviceError,
  } = useContestService();

  const { getFeedbackForms: getFeedbackFormsService } =
    useFeedbackFormService();
  const { language } = useLanguage();
  // ✅ Content für die aktuelle Sprache
  const content = giveAwayContent[language].giveawayForm;

  // ✅ Stabilize getFeedbackForms with useCallback
  const getFeedbackForms = useCallback(() => {
    return getFeedbackFormsService();
  }, [getFeedbackFormsService]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    status: "upcoming",
    feedbackFormId: "", // ✅ Neue Eigenschaft für Formular-Verbindung
    prizes: [
      {
        place: 1,
        title: "1.",
        description: "",
        value: "",
        voucherCode: "",
      },
    ],
    requiresName: true,
    requiresEmail: false,
    allowMultipleEntries: false,
    autoDrawEnabled: true,
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [feedbackForms, setFeedbackForms] = useState([]); // ✅ Verfügbare Formulare
  const [loadingForms, setLoadingForms] = useState(false);
  const submissionInProgress = useRef(false);
  // Kombiniere loading states
  const isLoading = loading || serviceLoading || isDeleting || loadingForms;

  const checkForActiveContests = () => {
    console.log(feedbackForms);
    if (!feedbackForms || feedbackForms.length === 0) {
      return { canActivate: true, activeContests: [] };
    }

    // Suche aktive Contests in den Feedback Forms (hasContest property)
    const activeContests = feedbackForms.filter(
      (form) =>
        form.hasContest &&
        (!editingGiveaway || form.contestId !== editingGiveaway._id)
    );
    console.log(activeContests);

    return {
      canActivate: activeContests.length === 0,
      activeContests,
    };
  };

  const handleStatusChange = (newStatus) => {
    handleInputChange("status", newStatus);
  };

  // ===== LOAD FEEDBACK FORMS =====
  useEffect(() => {
    let isMounted = true;

    const loadFeedbackForms = async () => {
      // ✅ Nur laden wenn Modal offen ist und noch keine Formulare geladen wurden
      if (!open || feedbackForms.length > 0 || loadingForms) {
        return;
      }

      console.log("🔄 Loading feedback forms..."); // Debug
      setLoadingForms(true);

      try {
        const forms = await getFeedbackForms();
        console.log("✅ Forms loaded:", forms); // Debug

        if (isMounted) {
          setFeedbackForms(forms || []);
        }
      } catch (error) {
        console.error("❌ Error loading feedback forms:", error);
        if (isMounted) {
          setFeedbackForms([]); // ✅ Leeres Array setzen bei Fehler
        }
      } finally {
        console.log("🏁 Setting loadingForms to false"); // Debug
        if (isMounted) {
          setLoadingForms(false);
        }
      }
    };

    loadFeedbackForms();

    return () => {
      isMounted = false;
    };
  }, [open]); // ✅ Nur 'open' als Dependency - sonst Loop!

  // ===== FORM RESET =====
  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      startDate: "",
      endDate: "",
      status: "upcoming",
      feedbackFormId: "", // ✅ Reset auch die Formular-ID
      prizes: [
        {
          place: 1,
          title: "1.",
          description: "",
          value: "",
          voucherCode: "",
        },
      ],
      requiresName: true,
      requiresEmail: false,
      allowMultipleEntries: false,
      autoDrawEnabled: true,
    });
    setValidationErrors({});
  };

  // ===== EFFECT FOR EDITING =====
  useEffect(() => {
    if (open) {
      if (editingGiveaway) {
        // Format dates for input fields
        console.log(editingGiveaway);
        const startDate =
          editingGiveaway.startDate instanceof Date
            ? editingGiveaway.startDate.toISOString().split("T")[0]
            : editingGiveaway.startDate?.split("T")[0] || "";

        const endDate =
          editingGiveaway.endDate instanceof Date
            ? editingGiveaway.endDate.toISOString().split("T")[0]
            : editingGiveaway.endDate?.split("T")[0] || "";

        setFormData({
          ...editingGiveaway,
          startDate,
          endDate,
          feedbackFormId: editingGiveaway.feedbackForm._id || "",
          prizes: editingGiveaway.prizes || [
            {
              place: 1,
              title: getPlaceText(1, language),
              description: "",
              value: "",
              voucherCode: "",
            },
          ],
        });
      } else {
        resetForm();
      }
      setValidationErrors({});
    } else {
      // ✅ Kompletter Reset beim Schließen
      console.log("🔄 Modal closed - resetting state"); // Debug
      setFeedbackForms([]);
      setLoadingForms(false);
      resetForm();
    }
  }, [editingGiveaway, open, language]);

  // ===== ERROR HANDLING =====
  useEffect(() => {
    if (serviceError) {
      const errorMessage = replaceTemplate(content.messages.serviceError, {
        error: serviceError.message || serviceError,
      });
      message.error(errorMessage);
    }
  }, [serviceError]);

  useEffect(() => {
    if (!open) {
      // ✅ Reset submission flag when modal closes
      submissionInProgress.current = false;
      // ... rest of cleanup
    }
  }, [open]);

  // ===== VALIDATION =====
  const validateForm = () => {
    const errors = {};

    // Title validation
    if (!formData.title?.trim()) {
      errors.title = content.validation.titleRequired;
    } else if (formData.title.length < 3) {
      errors.title = content.validation.titleMinLength;
    }

    // Description validation
    if (!formData.description?.trim()) {
      errors.description = content.validation.descriptionRequired;
    } else if (formData.description.length < 10) {
      errors.description = content.validation.descriptionMinLength;
    }

    // ✅ Feedback Form validation
    if (!formData.feedbackFormId) {
      errors.feedbackFormId = content.validation.feedbackFormRequired;
    }

    // Date validation
    if (!formData.startDate) {
      errors.startDate = content.validation.startDateRequired;
    }

    if (!formData.endDate) {
      errors.endDate = content.validation.endDateRequired;
    }

    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      const now = new Date();

      if (endDate <= startDate) {
        errors.endDate = content.validation.endDateAfterStart;
      }

      if (!editingGiveaway && startDate < now.setHours(0, 0, 0, 0)) {
        errors.startDate = content.validation.startDateNotPast;
      }
    }

    // Prizes validation
    if (!formData.prizes || formData.prizes.length === 0) {
      errors.prizes = content.validation.minOnePrize;
    } else {
      formData.prizes.forEach((prize, index) => {
        if (!prize.description?.trim()) {
          errors[`prize_${index}_description`] = replaceTemplate(
            content.validation.prizeDescriptionRequired,
            { title: prize.title }
          );
        }
        if (!prize.value?.trim()) {
          errors[`prize_${index}_value`] = replaceTemplate(
            content.validation.prizeValueRequired,
            { title: prize.title }
          );
        }
      });

      // Check for duplicate places
      const places = formData.prizes.map((p) => p.place);
      const duplicatePlaces = places.filter(
        (place, index) => places.indexOf(place) !== index
      );
      if (duplicatePlaces.length > 0) {
        errors.prizes = content.validation.duplicatePlaces;
      }
    }

    return errors;
  };

  // ===== FORM HANDLERS =====
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handlePrizeChange = (index, field, value) => {
    const newPrizes = [...formData.prizes];
    newPrizes[index] = { ...newPrizes[index], [field]: value };
    setFormData((prev) => ({ ...prev, prizes: newPrizes }));

    // Clear validation error for this prize field
    const errorKey = `prize_${index}_${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const addPrize = () => {
    const newPlace = formData.prizes.length + 1;
    const newPrize = {
      place: newPlace,
      title: `${formData.prizes.length + 1}.`,
      description: "",
      value: "",
      voucherCode: "",
    };
    setFormData((prev) => ({
      ...prev,
      prizes: [...prev.prizes, newPrize],
    }));
  };

  // Noch sicherere Alternative ohne Abhängigkeit von getPlaceText:

  const removePrize = (index) => {
    if (formData.prizes.length > 1) {
      const newPrizes = formData.prizes.filter((_, i) => i !== index);

      // Reorder places - ohne getPlaceText
      const reorderedPrizes = newPrizes.map((prize, i) => ({
        ...prize,
        place: i + 1,
        title: `${i + 1}.`, // Einfache Nummerierung
      }));

      setFormData((prev) => ({ ...prev, prizes: reorderedPrizes }));

      // Validation errors für gelöschte Preise aufräumen
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        // Entferne Validation-Fehler für alle Preise nach dem aktuellen Index
        Object.keys(newErrors).forEach((key) => {
          if (
            key.startsWith("prize_") &&
            parseInt(key.split("_")[1]) >= index
          ) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
  };

  // ===== SUBMIT HANDLER =====
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submissionInProgress.current) {
      console.warn("⚠️ Submission already in progress, ignoring duplicate");
      return;
    }

    setIsValidating(true);
    const errors = validateForm();
    setValidationErrors(errors);
    setIsValidating(false);

    if (Object.keys(errors).length > 0) {
      message.error(content.validation.correctErrors);
      return;
    }

    if (contestService?.validateContestData) {
      try {
        const serviceErrors = contestService.validateContestData(formData);
        if (serviceErrors.length > 0) {
          message.error(serviceErrors.join(", "));
          return;
        }
      } catch (error) {
        console.error("Validation error:", error);
      }
    }

    submissionInProgress.current = true;

    try {
      let result;

      // ❌ ALTE VALIDIERUNG ENTFERNT - Backend macht das jetzt!

      if (editingGiveaway) {
        result = await updateContest(editingGiveaway._id, formData);
        if (result.success) {
          message.success(content.messages.updateSuccess);
          resetForm();
          onSave(result.data, true);
          onCancel();
        }
      } else {
        result = await createContest(formData);
        if (result.success) {
          message.success(content.messages.createSuccess);
          resetForm();
          onSave(result.data, false);
          onCancel();
        } else {
          throw new Error(result.message || content.messages.createError);
        }
      }
    } catch (error) {
      console.error("Submit error:", error);

      // ✅ Spezielle Behandlung für "Contest bereits aktiv"
      if (error.response?.data?.error === "CONTEST_ALREADY_ACTIVE") {
        const conflictTitle =
          error.response.data.conflictingContest?.title || "Unbekannt";
        const errorMessage =
          language === "de"
            ? `Für dieses Formular ist bereits der Contest "${conflictTitle}" aktiv. Bitte deaktiviere diesen zuerst.`
            : `Contest "${conflictTitle}" is already active for this form. Please deactivate it first.`;

        message.error(errorMessage, 6);
        submissionInProgress.current = false;
        return;
      }

      const errorMessage = replaceTemplate(content.messages.saveError, {
        error: error.message || error,
      });
      message.error(errorMessage);
    } finally {
      submissionInProgress.current = false;
    }
  };
  // ===== DELETE HANDLER =====
  const handleDelete = async () => {
    if (!editingGiveaway?.id) return;

    if (window.confirm(content.info.deleteConfirm)) {
      setIsDeleting(true);
      try {
        const result = await deleteContest(editingGiveaway.id);
        if (result.success) {
          message.success(content.messages.deleteSuccess);
          resetForm();
          onCancel(); // ✅ Modal schließen
          // Notify parent component about deletion
          if (onSave) {
            onSave(null, true, "deleted");
          }
        }
      } catch (error) {
        console.error("Delete error:", error);
        const errorMessage = replaceTemplate(content.messages.deleteError, {
          error: error.message || error,
        });
        message.error(errorMessage);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  // ===== CANCEL HANDLER =====
  const handleCancel = () => {
    if (!isLoading) {
      resetForm();
      onCancel();
    }
  };

  // ===== RENDER =====
  if (!open) return null;

  const isEditing = !!editingGiveaway;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">
            {isEditing ? content.editTitle : content.createTitle}
          </h2>
          <div className="flex items-center gap-4">
            {isLoading && (
              <div className="flex items-center gap-2 text-orange-600">
                <Spin size="small" />
                <span className="text-sm">
                  {isDeleting
                    ? content.deleting
                    : loadingForms
                    ? content.loadingForms
                    : content.saving}
                </span>
              </div>
            )}
            {isEditing && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <Spin size="small" />
                    {content.deleting}
                  </>
                ) : (
                  <>{content.delete}</>
                )}
              </button>
            )}
            {/* ✅ X Close Button */}
            <button
              type="button"
              onClick={handleCancel}
              disabled={isLoading}
              className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              title={content.close}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              {content.basicInfo}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {content.titleRequired}
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  disabled={isLoading}
                  className={`w-full border rounded-md px-3 py-2 disabled:bg-gray-100 ${
                    validationErrors.title
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder={content.titlePlaceholder}
                  required
                />
                {validationErrors.title && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.title}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {content.status}
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={isLoading}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100"
                >
                  <option value="upcoming">{content.statusUpcoming}</option>
                  <option value="active">{content.statusActive}</option>
                  <option value="ended">{content.statusEnded}</option>
                  <option value="drawn">{content.statusDrawn}</option>
                </select>
                {formData.status === "active" && (
                  <p className="text-orange-600 text-xs mt-1">
                    {language === "de"
                      ? "Nur ein Contest kann gleichzeitig aktiv sein"
                      : "Only one contest can be active at a time"}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">
                {content.descriptionRequired}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                disabled={isLoading}
                className={`w-full border rounded-md px-3 py-2 disabled:bg-gray-100 ${
                  validationErrors.description
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                rows={3}
                placeholder={content.descriptionPlaceholder}
                required
              />
              {validationErrors.description && (
                <p className="text-red-500 text-xs mt-1">
                  {validationErrors.description}
                </p>
              )}
            </div>

            {/* ✅ Feedback Form Selection */}
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">
                {content.feedbackFormRequired}
              </label>
              <select
                value={formData.feedbackFormId}
                onChange={(e) =>
                  handleInputChange("feedbackFormId", e.target.value)
                }
                disabled={isLoading}
                className={`w-full border rounded-md px-3 py-2 disabled:bg-gray-100 ${
                  validationErrors.feedbackFormId
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                required
              >
                <option value="">{content.feedbackFormPlaceholder}</option>
                {feedbackForms.map((form) => (
                  <option key={form._id} value={form._id}>
                    {form.name || form.title || `Formular ${form._id}`}
                  </option>
                ))}
              </select>
              {validationErrors.feedbackFormId && (
                <p className="text-red-500 text-xs mt-1">
                  {validationErrors.feedbackFormId}
                </p>
              )}
              {feedbackForms.length === 0 && !loadingForms && (
                <p className="text-orange-600 text-xs mt-1">
                  {content.info.noFormsAvailable}
                </p>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
              {content.timeframe}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">
                  {content.startDateRequired}
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) =>
                    handleInputChange("startDate", e.target.value)
                  }
                  disabled={isLoading}
                  className={`w-full border rounded-md px-3 py-2 disabled:bg-gray-100 ${
                    validationErrors.startDate
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  required
                />
                {validationErrors.startDate && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.startDate}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">
                  {content.endDateRequired}
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange("endDate", e.target.value)}
                  disabled={isLoading}
                  className={`w-full border rounded-md px-3 py-2 disabled:bg-gray-100 ${
                    validationErrors.endDate
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  required
                />
                {validationErrors.endDate && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.endDate}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Prizes */}
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-orange-800 flex items-center gap-2">
                {content.prizes}
              </h3>
              <button
                type="button"
                onClick={addPrize}
                disabled={isLoading || formData.prizes.length >= 10}
                className="px-3 py-1 bg-orange-500 text-white rounded text-sm hover:bg-orange-600 disabled:opacity-50"
              >
                {content.addPrize}
              </button>
            </div>

            {validationErrors.prizes && (
              <p className="text-red-500 text-xs mb-3">
                {validationErrors.prizes}
              </p>
            )}

            <div className="space-y-4">
              {formData.prizes.map((prize, index) => (
                <div key={index} className="bg-white p-4 rounded border">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-800">{prize.title}</h4>
                    {formData.prizes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePrize(index)}
                        disabled={isLoading}
                        className="text-red-500 hover:text-red-700 disabled:opacity-50"
                      >
                        {content.removePrize}
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {content.prizeDescriptionRequired}
                      </label>
                      <input
                        type="text"
                        value={prize.description}
                        onChange={(e) =>
                          handlePrizeChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        disabled={isLoading}
                        className={`w-full border rounded px-3 py-2 disabled:bg-gray-100 ${
                          validationErrors[`prize_${index}_description`]
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder={content.prizeDescriptionPlaceholder}
                        required
                      />
                      {validationErrors[`prize_${index}_description`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors[`prize_${index}_description`]}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {content.prizeValueRequired}
                      </label>
                      <input
                        type="text"
                        value={prize.value}
                        onChange={(e) =>
                          handlePrizeChange(index, "value", e.target.value)
                        }
                        disabled={isLoading}
                        className={`w-full border rounded px-3 py-2 disabled:bg-gray-100 ${
                          validationErrors[`prize_${index}_value`]
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder={content.prizeValuePlaceholder}
                        required
                      />
                      {validationErrors[`prize_${index}_value`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors[`prize_${index}_value`]}
                        </p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {content.voucherCode}
                      </label>
                      <input
                        type="text"
                        value={prize.voucherCode}
                        onChange={(e) =>
                          handlePrizeChange(
                            index,
                            "voucherCode",
                            e.target.value
                          )
                        }
                        disabled={isLoading}
                        className="w-full border border-gray-300 rounded px-3 py-2 disabled:bg-gray-100 font-mono"
                        placeholder={content.voucherCodePlaceholder}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isLoading}
              className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {content.cancel}
            </button>

            <button
              type="submit"
              disabled={isLoading || isValidating || feedbackForms.length === 0}
              className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Spin size="small" />
                  {loadingForms ? content.loading : content.saving}
                </>
              ) : (
                <>{isEditing ? content.update : content.create}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
